import { isNil, map } from "lodash";
import {
  AICC_SANDBOX_ACTION,
  AiccSandboxAction,
  AiccSandboxDirective,
  AiccSandboxEndReason,
  AiccSandboxGradingOutcome,
} from "constants/aiccPromptSandbox";
import {
  AiccSandboxActionResponse,
  AiccSandboxCostResponse,
  AiccSandboxCursorResponse,
  AiccSandboxFlowUsedResponse,
  AiccSandboxGradingResponse,
  AiccSandboxFeedbackResponse,
  AiccSandboxGrammarResponse,
  AiccSandboxPromptUsedResponse,
  AiccSandboxRenderedPromptResponse,
  AiccSandboxRenderResponse,
  AiccSandboxSettingsResponse,
  AiccSandboxTurnResponse,
  AiccSandboxUsageResponse,
} from "types/dto/response/aiccPromptSandbox";
import {
  AiccSandboxActionPayload,
  AiccSandboxCost,
  AiccSandboxActionResult,
  AiccSandboxCursor,
  AiccSandboxFlowUsed,
  AiccSandboxGrading,
  AiccSandboxFeedbackResult,
  AiccSandboxGrammarResult,
  AiccSandboxPromptUsed,
  AiccSandboxRender,
  AiccSandboxRunFacts,
  AiccSandboxSettings,
  AiccSandboxTurnResult,
  AiccSandboxUsage,
} from "types/model/aiccPromptSandbox";

/**
 * 🔴 NOTHING IS INVENTED WHERE THE BACKEND SAID NOTHING — in particular the transliteration fields, since a
 * romaji line borrowed from the text beside it would read as cutlet's output and be believed.
 */

const transformSettings = (response?: AiccSandboxSettingsResponse | null): AiccSandboxSettings => ({
  modelId: response?.model_id || undefined,
  reasoningEffort: response?.llm_reasoning_effort || undefined,
  // `null` is "the parameter was not sent"; 0 is the strictest setting there is.
  temperature: isNil(response?.temperature) ? undefined : (response?.temperature as number),
});

const count = (value?: number | null) => (isNil(value) ? undefined : value);

const transformUsage = (response?: AiccSandboxUsageResponse | null): AiccSandboxUsage | undefined =>
  isNil(response)
    ? undefined
    : {
        inputTokens: count(response.input_tokens),
        cachedInputTokens: count(response.cached_input_tokens),
        cacheWriteTokens: count(response.cache_write_tokens),
        outputTokens: count(response.output_tokens),
        reasoningTokens: count(response.reasoning_tokens),
      };

/** 🔴 `usd` STAYS ABSENT WHERE IT WAS NULL: that is "no rate for the model that ran", which is not a price of zero. */
const transformCost = (response?: AiccSandboxCostResponse | null): AiccSandboxCost | undefined =>
  isNil(response)
    ? undefined
    : {
        usd: count(response.usd),
        models: response.models ?? [],
        unpricedModels: response.unpriced_models ?? [],
      };

const transformPromptCoordinate = (response: AiccSandboxRenderedPromptResponse) => ({
  templateId: response.template_id,
  source: response.source as AiccSandboxPromptUsed["source"],
  textSource: response.text_source || undefined,
  flowVersion: isNil(response.flow_version) ? undefined : response.flow_version,
});

const transformPromptUsed = (response: AiccSandboxPromptUsedResponse): AiccSandboxPromptUsed => ({
  ...transformPromptCoordinate(response),
  role: response.role,
});

const transformRunFacts = (response: AiccSandboxTurnResponse | AiccSandboxActionResponse): AiccSandboxRunFacts => ({
  promptsUsed: map(response.prompts_used, transformPromptUsed),
  effective: transformSettings(response.effective),
  // 🔴 Absent unless it differs from `effective` — that IS the signal, so never fill it in from there.
  requested: isNil(response.requested) ? undefined : transformSettings(response.requested),
  usage: transformUsage(response.usage),
  cost: transformCost(response.cost),
});

const transformGrammar = (grammar: AiccSandboxGrammarResponse): AiccSandboxGrammarResult => ({
  hasErrors: !!grammar.are_there_any_errors,
  correctedMessage: grammar.corrected_message || undefined,
  highlightedErrors: grammar.highlighted_errors ?? [],
});

/** 🔴 Kept exactly as served, because it goes straight back out on the next turn (§4.7.2). */
const transformCursor = (response?: AiccSandboxCursorResponse | null): AiccSandboxCursor | undefined =>
  isNil(response)
    ? undefined
    : {
        lineIdx: response.line_idx ?? 0,
        retryCount: response.retry_count ?? 0,
        introEmitted: !!response.intro_emitted,
      };

/**
 * 🔴 A JUDGE THAT DID NOT RUN STAYS `undefined`, NOT `false`. `llm_judge_correct` and `is_giving_up` are null
 * whenever the jaccard fast path answered, and `!!null` would put "judged not correct" on screen for a judge that
 * was never consulted — sending the operator to edit a prompt that had no part in the verdict.
 */
const transformGrading = (response?: AiccSandboxGradingResponse | null): AiccSandboxGrading | undefined =>
  isNil(response)
    ? undefined
    : {
        processedText: response.processed_text ?? "",
        processedExpected: response.processed_expected ?? "",
        // Null is "nothing to score" (an empty answer), which is not a score of zero.
        jaccardScore: count(response.jaccard_score),
        jaccardThreshold: response.jaccard_threshold ?? 0,
        isJaccardPassed: !!response.jaccard_passed,
        llmJudgeCorrect: isNil(response.llm_judge_correct) ? undefined : response.llm_judge_correct,
        isGivingUp: isNil(response.is_giving_up) ? undefined : response.is_giving_up,
        expected: response.expected ?? "",
        outcome: (response.outcome as AiccSandboxGradingOutcome) || undefined,
        retryCount: response.retry_count ?? 0,
        maxRetries: response.max_retries ?? 0,
      };

const transformFlowUsed = (response: AiccSandboxFlowUsedResponse): AiccSandboxFlowUsed => ({
  flowId: response.flow_id ?? "",
  effective: transformSettings(response.effective),
  requested: isNil(response.requested) ? undefined : transformSettings(response.requested),
  isPinned: !!response.pinned,
});

export function transformAiccSandboxTurn(response: AiccSandboxTurnResponse): AiccSandboxTurnResult {
  return {
    ...transformRunFacts(response),
    reply: response.reply ?? "",
    // Kept apart rather than defaulted to each other: a wrap-up turn is where the two differ.
    sentUserContent: response.sent_user_content ?? "",
    historyUserContent: response.history_user_content ?? "",
    opener: response.opener?.text
      ? {
          text: response.opener.text,
          translation: response.opener.translation || undefined,
          transliteration: response.opener.transliteration || undefined,
        }
      : undefined,
    // 🔴 EITHER FLAG ENDS IT: watching only one lets an operator chat past the round where production would
    // have stopped them, and conclude their wrap-up prompt does nothing.
    isEnded: !!response.end_conversation || !!response.forced_end,
    isForcedEnd: !!response.forced_end,
    endReason: (response.end_reason as AiccSandboxEndReason) || undefined,
    isWrapupArmed: !!response.wrapup_armed,
    appendedDirective: (response.appended_directive as AiccSandboxDirective) || undefined,
    roundCount: response.round_count ?? 0,
    translation: response.translation || undefined,
    transliteration: response.transliteration || undefined,
    userNativeText: response.user_native_text || undefined,
    userNativeMap: response.user_native_map ?? undefined,
    cursor: transformCursor(response.cursor),
    grading: transformGrading(response.grading),
    grammar: isNil(response.grammar) ? undefined : transformGrammar(response.grammar),
    flowsUsed: map(response.flows_used, transformFlowUsed),
  };
}

const transformFeedback = (feedback: AiccSandboxFeedbackResponse): AiccSandboxFeedbackResult => ({
  summary: feedback.feedback_summary || undefined,
  achievements: feedback.achievements ?? [],
  suggestions: feedback.suggestions ?? [],
});

/**
 * 🔴 THE ACTION DECIDES WHICH FIELD IS THE ANSWER. The response carries all four, exactly one of them non-null, so
 * reading the wrong one is indistinguishable from an empty result — and the caller always knows which it asked for.
 *
 * 🔴 A NULL SURVIVES AS A NULL. The hint service degrades to none when its prompt is misconfigured, and the
 * feedback prompt can come back with nothing; a placeholder here would show the operator a result their prompt
 * never produced.
 */
export function transformAiccSandboxAction(
  action: AiccSandboxAction,
  response: AiccSandboxActionResponse
): AiccSandboxActionResult {
  const payloads: Record<AiccSandboxAction, () => AiccSandboxActionPayload | undefined> = {
    [AICC_SANDBOX_ACTION.hint]: () => (isNil(response.hint) ? undefined : { hint: response.hint }),
    [AICC_SANDBOX_ACTION.errorCorrection]: () =>
      isNil(response.grammar) ? undefined : transformGrammar(response.grammar),
    [AICC_SANDBOX_ACTION.feedback]: () => (isNil(response.feedback) ? undefined : transformFeedback(response.feedback)),
    [AICC_SANDBOX_ACTION.foreignWordIdentification]: () =>
      isNil(response.foreign_words)
        ? undefined
        : {
            words: map(response.foreign_words, (word) => ({
              sourceText: word.source_text ?? "",
              targetText: word.target_text ?? "",
            })),
          },
  };

  return {
    ...transformRunFacts(response),
    renderedSystemPrompt: response.rendered_system_prompt || undefined,
    notSimulatedVariables: response.not_simulated_variables ?? [],
    notSimulatedBehaviours: response.not_simulated_behaviours ?? [],
    result: payloads[action](),
  };
}

export const transformAiccSandboxRender = (response?: AiccSandboxRenderResponse): AiccSandboxRender => ({
  prompts: map(response?.prompts, (prompt) => ({
    ...transformPromptCoordinate(prompt),
    displayName: prompt.display_name || prompt.template_id,
    rendered: prompt.rendered ?? "",
  })),
  notSimulatedVariables: response?.not_simulated_variables ?? [],
  notSimulatedBehaviours: response?.not_simulated_behaviours ?? [],
});
